import { request } from './client'
import type { StaffAuth } from './teaching'
import type { AgeBand, ContentSkill, MotorSkill } from '../types/prisma-enums'

/**
 * D5's reads and the four teacher controls — T1, T4, T7 and the class move.
 *
 * 🔴 `teaching/controls.controller.ts` IS AN ENTIRE `@Roles('TEACHER','ADMIN')`
 * CONTROLLER THAT NOTHING CALLED. Four routes, built, scoped and tested, with
 * no UI on the other end — and one of them is not a feature at all.
 * `information-architecture.md` §7 files T1 under *"correctness-or-rights gaps
 * rather than features"*: **"tapping the wrong roll number silently sends wrong
 * data to a parent."* A teacher who mis-taps today has no way to fix it, and the
 * child's parent reads someone else's afternoon.
 *
 * ⚠️ EVERY CALL IS SCOPED SERVER-SIDE TO THE CALLER'S OWN CHILDREN. `ScopeService`
 * decides; nothing here filters. A teacher naming another class's `playerId`
 * gets a 403, and that is the control — not the fact that this module is only
 * imported by teacher screens.
 */

/* ────────────────────────────────────────────────────────── reads ── */

export interface SkillTally {
  skill: ContentSkill | MotorSkill
  /** 🔴 An event COUNT, never a percentage — OD-39. */
  events: number
}

export interface ContentSkillTally extends SkillTally {
  skill: ContentSkill
  /**
   * 🔴 CONTENT SKILLS READ AS A LEVEL, NOT A PERCENTAGE. `ENGINEERING-GUIDE.md` §8:
   * *"prefer 'Numbers · Level 3' over '45% numeracy'"* — a percentage of a
   * curriculum is a grade nobody agreed the denominator for.
   */
  levelCompleted: number
}

export interface MotorSkillTally extends SkillTally {
  skill: MotorSkill
}

export interface SkillBreakdown {
  content: ContentSkillTally[]
  motor: MotorSkillTally[]
  topSkill: ContentSkill | null
  topMotorSkill: MotorSkill | null
  totalEvents: number
}

export interface ProgressSnapshot {
  playerId: string
  coins: number
  stickers: string[]
  streakDays: number
  lastPlayed: string | null
  skills: SkillBreakdown
}

/**
 * `GET /progress/:playerId`.
 *
 * ⚠️ ZEROES RATHER THAN A 404 when the child has never played — the controller
 * says so explicitly. So an empty snapshot is a real answer about a real child,
 * and the screen must render it as "not started" rather than as a failure.
 */
export const getPlayerProgress = (playerId: string, auth: StaffAuth = {}): Promise<ProgressSnapshot> =>
  request<ProgressSnapshot>(`/progress/${encodeURIComponent(playerId)}`, {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/** One sitting. What T1 reassigns — and therefore what a teacher must see. */
export interface SessionRow {
  id: string
  startedAt: string
  /** Null while a session is open, or when nothing closed it (OD-37). */
  endedAt: string | null
  /**
   * ADR-030: `false` means events were never written at all. So zero events is
   * not the same fact as "this child did nothing", and the screen must not
   * present them as if they were.
   */
  recorded: boolean
  events: number
}

/**
 * `GET /players/:playerId/sessions`.
 *
 * 🔴 THIS READ WAS ADDED BECAUSE T1 WAS UNREACHABLE WITHOUT IT. The reassign
 * route shipped scoped and tested; nothing could list a session id, `EventRow`
 * carries none, and there is no `GET /sessions`. A correctness fix nobody can
 * invoke is not a correctness fix.
 */
export const getPlayerSessions = (playerId: string, auth: StaffAuth = {}): Promise<SessionRow[]> =>
  request<SessionRow[]>(`/players/${encodeURIComponent(playerId)}/sessions`, {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/* ───────────────────────────────────────────────────────── writes ── */

const send = <T>(
  path: string,
  method: 'POST' | 'PATCH' | 'DELETE',
  body: unknown,
  auth: StaffAuth,
): Promise<T> =>
  request<T>(path, { method, body, context: 'user', token: auth.token, signal: auth.signal })

/**
 * 🔴 T1 — THE ONE THAT IS A CORRECTNESS FIX, NOT A FEATURE.
 *
 * `POST /sessions/:sessionId/reassign`. A teacher taps roll 03 and hands the
 * laptop to roll 13; without this the session, its events and its progress are
 * permanently attributed to a child who was not there — and the weekly digest
 * tells that child's parent about it.
 *
 * ⚠️ IT MOVES A WHOLE SESSION, NOT ONE EVENT. `Session.playerId` is the mutable
 * field the schema comment calls out as *"what teacher reassignment (T1)
 * rewrites"*; events follow their session.
 */
export interface ReassignResult {
  sessionId: string
  from: string
  to: string
  eventsMoved: number
  /**
   * 🔴 ALWAYS FALSE, AND THE SCREEN MUST SAY SO. The server's own comment:
   * coins, stickers and streaks are **device-authored** and there is no
   * per-session ledger, so reassigning a sitting moves its events and cannot
   * move what those events were worth. A teacher who assumes otherwise will
   * believe a leaderboard has been corrected when it has not.
   */
  rewardsMoved: boolean
}

export const reassignSession = (
  sessionId: string,
  toPlayerId: string,
  auth: StaffAuth = {},
): Promise<ReassignResult> =>
  send(`/sessions/${encodeURIComponent(sessionId)}/reassign`, 'POST', { toPlayerId }, auth)

/**
 * T4 — a per-child band override.
 *
 * 🔴 A BAND IS A DEFAULT, NOT A DIAGNOSIS. `org.prisma` says it in those words:
 * the class band decides which games are *offered*, and a teacher may override
 * it for one child without moving the class. What it changes is the content
 * that child is given next, so the screen must say that rather than implying an
 * assessment has been recorded.
 */
export const setPlayerBand = (
  playerId: string,
  ageBand: AgeBand,
  auth: StaffAuth = {},
): Promise<{ playerId: string; ageBand: string }> =>
  send(`/players/${encodeURIComponent(playerId)}/band`, 'PATCH', { ageBand }, auth)

/**
 * 🔴 T7 — AND THE LOGIC BEHIND IT HAS BEEN BUILT AND UNREACHABLE ALL ALONG.
 *
 * `attention.ts` already discounts absent days from the idle count — *"a child
 * who was away on Monday and Tuesday has not been idle for two days in any
 * sense a teacher cares about"* — and **nothing in the product could write an
 * absence**. So the discount has never once applied, and every absent child has
 * been reported to their teacher as needing chasing.
 *
 * ⚠️ IT MARKS TODAY, and the server stamps the day — the client does not choose
 * the date. A teacher marking Monday's absence on Wednesday is a different
 * feature (a date picker) and a different conversation about back-dating.
 */
export const markAbsentToday = (
  playerId: string,
  auth: StaffAuth = {},
): Promise<{ playerId: string; day: string }> =>
  send(`/players/${encodeURIComponent(playerId)}/absent`, 'POST', undefined, auth)

/** Undo today's mark — the same day, removed. A mis-tap must be reversible. */
export const unmarkAbsentToday = (playerId: string, auth: StaffAuth = {}): Promise<void> =>
  send(`/players/${encodeURIComponent(playerId)}/absent`, 'DELETE', undefined, auth)

/**
 * Correct the number a child signs in with.
 *
 * 🔴 A SEPARATE CALL FROM `movePlayerClass`, WHICH ALSO CARRIES A ROLL NUMBER.
 * That one moves a child between sections and renumbers them as a consequence;
 * this one renumbers and moves nobody. A register typo is the ordinary
 * correction, and making it a request that names a destination class means a
 * wrong value in that field moves a child out of the room they are sitting in.
 *
 * ⚠️ THE ROLL NUMBER IS WHAT `kid-auth` LOOKS A CHILD UP BY, so a wrong one is
 * not a cosmetic error on a register — it is a child who cannot sign in, and
 * until this existed a teacher had no way to fix it short of moving them out of
 * the class and back in.
 *
 * ⚠️ `null` CLEARS IT, AND THAT IS A REAL STATE. A child who arrives before the
 * office assigns a roll is on the register that morning; what they cannot do is
 * sign in, and the screen says so rather than letting a teacher find out at
 * 10:05.
 *
 * ⚠️ A 409 IS THE COLLISION, NOT A FAILURE. `@@unique([classId, rollNumber])`
 * is *"the hinge of the entire four-tier product"*, and the server answers with
 * a sentence naming the child already holding the number. Renumbering a register
 * is exactly when two children pass through one number, so the screen must show
 * that sentence rather than a generic error.
 */
export const setPlayerRollNumber = (
  playerId: string,
  rollNumber: string | null,
  auth: StaffAuth = {},
): Promise<{ playerId: string; rollNumber: string | null }> =>
  send(`/players/${encodeURIComponent(playerId)}/roll`, 'PATCH', { rollNumber }, auth)

/**
 * Move a child into another class.
 *
 * ⚠️ THE ROLL NUMBER TRAVELS WITH THEM, AND MAY HAVE TO CHANGE. Roll numbers
 * are unique per class (`@@unique([classId, rollNumber])`), so moving roll 03
 * into a class that already has one must either rename or fail. The optional
 * `rollNumber` is how the caller resolves that; omitting it keeps the current
 * one and lets the server refuse the collision.
 */
export const movePlayerClass = (
  playerId: string,
  body: { classId: string; rollNumber?: string | null },
  auth: StaffAuth = {},
): Promise<{ playerId: string; classId: string; rollNumber: string | null }> =>
  send(`/players/${encodeURIComponent(playerId)}/class`, 'PATCH', body, auth)

/**
 * Put one child on a register.
 *
 * 🔴 IT IS NOT THE CSV IMPORT WITH ONE ROW. `POST /roster/import` is ADMIN-only
 * against threat T8 — it moves hundreds of parent phone numbers behind one audit
 * row — and until this existed the ONLY way to create a child was that
 * spreadsheet. A teacher who gained one child in October had to find an admin,
 * who had to re-export a file, to write down a name.
 *
 * ✅ THE PARENT FIELDS NEED `roster` TOO, AND THIS PARAGRAPH USED TO SAY THEY
 * NEEDED `write`. It read *"the screen that offers the parent fields is
 * therefore the admin's"*, which ADR-050 overturned: `addStudent` asks for one
 * level for the whole body, because *"the teacher is the person the parent hands
 * the number to at the gate"*. Both tiers draw the fields now, and
 * `linkPlayerParent` below exists on the same reasoning — leaving this note
 * standing would have it contradicting the function beneath it.
 *
 * ⚠️ THE STAFF-NUMBER REFUSAL IS WHAT MAKES THE WIDER LEVEL SAFE, not the
 * scope check. A number belonging to a non-PARENT account is rejected outright,
 * so no teacher can hand herself a parent's view of a child.
 */
export interface AddedStudent {
  playerId: string
  name: string
  rollNumber: string | null
  classId: string
  parentCreated: boolean
  parentLinked: boolean
}

export const addStudent = (
  body: {
    classId: string
    name: string
    rollNumber?: string | null
    /** ⚠️ ADMIN ONLY — see above. */
    parentName?: string | null
    /** ⚠️ ADMIN ONLY — see above. */
    parentPhone?: string | null
  },
  auth: StaffAuth = {},
): Promise<AddedStudent> => send('/players', 'POST', body, auth)

/**
 * Take a child off this register.
 *
 * 🔴 IT DELETES NOTHING, AND THE PATH SAYS SO. "Remove" in a classroom means
 * *"no longer in my section"* — they moved, they left, they were added twice.
 * Erasing what a child did is their PARENT's right (P2), it is irreversible, and
 * `Player` is also the school's enrolment record. The child becomes classless,
 * which is a state the school dashboard already counts and shows.
 */
export const removeFromClass = (
  playerId: string,
  auth: StaffAuth = {},
): Promise<{ playerId: string; classId: null }> =>
  send(`/players/${encodeURIComponent(playerId)}/remove-from-class`, 'POST', undefined, auth)

/**
 * Correct the spelling of a child's name.
 *
 * 🔴 THE REGISTER COULD BE RENUMBERED BUT NOT RESPELLED. `setPlayerRollNumber`
 * exists because a typo on a register is ordinary; a typo in a NAME had no route
 * at all, so the only fix was `POST /roster/import` — ADMIN-only against threat
 * T8 — and correcting one child's spelling meant an admin re-exporting a
 * spreadsheet.
 *
 * ⚠️ A SEPARATE PATCH FROM `/roll`, not one that carries both. A request that
 * also names a roll number is one whose stale field renumbers a child nobody
 * meant to touch.
 */
export const setPlayerName = (
  playerId: string,
  name: string,
  auth: StaffAuth = {},
): Promise<{ playerId: string; name: string }> =>
  send(`/players/${encodeURIComponent(playerId)}/name`, 'PATCH', { name }, auth)

/**
 * Link a family's phone number to a child already on the register.
 *
 * 🔴 THE PARENT HALF OF `addStudent`, FOR A CHILD WHO ALREADY EXISTS. A phone
 * number could only ever be attached at the moment a child was created; a child
 * imported without one — which the roster import permits — was parentless short
 * of re-importing the school. D5 draws *"Progress reports cannot be sent until a
 * parent is linked"*, and until this existed that sentence described a dead end.
 *
 * ⚠️ `roster` SCOPE, NOT `write` — ADR-050. The teacher is the person the parent
 * hands the number to at the gate. The bulk import stays ADMIN-only.
 *
 * ⚠️ A 409 IS THE STAFF-NUMBER REFUSAL, NOT A FAILURE. A number belonging to a
 * teacher or an admin is rejected with a sentence, because linking it would hand
 * that person a parent's view of a child.
 *
 * ⚠️ THERE IS NO UNLINK. Taking a family's access away is a rights change
 * `RightsService` owns, not a register correction; a wrong number is fixed by
 * sending the right one.
 */
export const linkPlayerParent = (
  playerId: string,
  body: { parentPhone: string; parentName?: string | null },
  auth: StaffAuth = {},
): Promise<{ playerId: string; parentCreated: boolean; parentLinked: boolean }> =>
  send(`/players/${encodeURIComponent(playerId)}/parent`, 'PATCH', body, auth)
